import { firstValueFrom } from "rxjs";
import { Injectable, inject } from "@angular/core";
import {
  ApiCallsService,
  CmpAuthService,
  EnvSetupObject,
} from "icamppro-shared-components";
import { HttpResponseObject } from "@iclassproinc/icamppro-shared-components";
import { AppRouterService } from "src/routing/routes-service";
import moment from "moment";
import { CMP_ENV_SETUP } from "src/app/cmp-env-setup.injection-token";

@Injectable({
  providedIn: "root",
})
export class MaintenanceModeService {
  private apiService = inject(ApiCallsService);
  private routerService = inject(AppRouterService);
  private authService = inject(CmpAuthService);
  cmpEnv = inject<EnvSetupObject>(CMP_ENV_SETUP);

  public maintenanceSetting = new CmpMaintenanceSetting();

  public async checkForMaintenance() {
    if (this.checkMaintenanceInLocalStorage()) {
      return;
    }

    const tenantInfo = await this.cmpEnv.getTenantInfo();
    if (!tenantInfo) {
      return;
    }
    const payload = {
      tenantId: tenantInfo.id,
    };
    const response: HttpResponseObject<CmpMaintenanceSetting> =
      await firstValueFrom(
        this.apiService.get(
          this.routerService.backendApiUrl.regPortalImmediateMaintenanceMode,
          payload,
        ),
      );
    if (!response.hasErrors && response.data) {
      const data: CmpMaintenanceSetting = response.data;
      this.maintenanceSetting = data;
      if (data.maintenanceMode) {
        this.gotoMaintenancePage();
        return;
      }
      if (data.scheduled) {
        this.checkIfTimerTriggered();
        return;
      }
      this.triggerMaintenanceCheck();
    }
  }

  private gotoMaintenancePage() {
    this.authService.logOut();
    this.maintenanceSetting.maintenanceMode = true;
  }

  /**
   * @method checkIfTimerTriggered
   * @description Checks if the final maintenance notification time was triggered
   */
  private checkIfTimerTriggered() {
    const scheduledTime = moment
      .tz(this.maintenanceSetting.scheduledTime, "America/Chicago")
      .local();
    const diffMinutes = scheduledTime.diff(moment(), "m");
    if (diffMinutes < 30) {
      this.startNotificationTimer(scheduledTime);
    } else {
      setTimeout(() => this.checkIfTimerTriggered(), 60000 * 5); // Check for timer every 5 minutes
    }
  }

  /**
   * @method triggerMaintenanceCheck
   * @description Checks if the maintenance mode was enabled in director portal for every 15 minutes
   */
  private triggerMaintenanceCheck() {
    const minute = 60000;
    setTimeout(() => {
      this.checkForMaintenance();
    }, minute * 15); // Check maintenance mode for every 15 minutes
  }

  private checkMaintenanceInLocalStorage() {
    const storageValue = localStorage.getItem("maintenanceSettings");

    if (storageValue !== null) {
      localStorage.removeItem("maintenanceSettings");
      const maintenanceSettings = JSON.parse(storageValue);
      this.maintenanceSetting = maintenanceSettings;
      return true;
    }
    return false;
  }

  /**
   * @method startNotificationTimer
   * @description Starts the final notification timer for maintenance schedule
   */
  private startNotificationTimer(scheduledTime: moment.Moment) {
    const interval = setInterval(() => {
      const pendingSeconds = scheduledTime.diff(new Date(), "s");
      if (pendingSeconds <= 0) {
        clearInterval(interval);
        this.gotoMaintenancePage();
      }
    }, 1000);
  }
}

class CmpMaintenanceSetting {
  maintenanceMode = false;
  scheduled = false;
  scheduledTime = "";
  title = "";
  message = "";
}
